import Big from "big.js";
import { PLATFORMS } from "../constants/AppConstants";
import { formatMoney } from "./numberUtils";
import { hasDiscount } from "./productUtils";
import { isEmpty } from "lodash";

export const getPlatformOptions = (platform) => {
  if (platform === PLATFORMS.WHATSAPP) {
    return {
      chatType: platform,
      platform: platform
    }
  }

  if (platform === PLATFORMS.INSTAGRAM_COMMENTS) {
    return {
      chatType: PLATFORMS.INSTAGRAM,
      platform: platform
    }
  }

  return {
    chatType: PLATFORMS.FACEBOOK,
    platform: platform
  }
}

export const isWhatsapp = (platform = '') => {
  if (!platform) return false;
  const p = String(platform).toLowerCase();
  return p === 'whatsapp' || p.startsWith('whatsapp');
}

export const isMessenger = (platform = '') => {
  return platform === PLATFORMS.MESSENGER;
}

export const isInstagram = (platform = '') => {
  return platform === PLATFORMS.INSTAGRAM;
}

export const messageFileType = (type = null, platform) => {
  if (platform == PLATFORMS.WHATSAPP) return wsFileType(type);

  if (platform == PLATFORMS.MESSENGER || platform == PLATFORMS.INSTAGRAM) {
    return fbFileType(type);
  }
};

export const classByMessageDirection = (message, platform) => {
  if (message.mia_client_data || message.mia_assignment || message.mia_event) { return 'w-perc-100'; }

  if (platform == PLATFORMS.MESSENGER || platform == PLATFORMS.INSTAGRAM) {
    let classes = message.sent_by_retailer === true ? `message-by-retailer f-right ${(message.error_message ? ' error-message' : '')}` : 'message-by-customer';
    classes += ' main-message-container';
    if (message.note === true && !message.order_note) classes += ' note-message';
    if (message.order_note) classes += ' order-note-message';
    if (['voice', 'audio'].includes(fbFileType(message.file_type))) classes += ' video-audio audio-background';
    if (fbFileType(message.file_type) === 'video') classes += ' video-audio no-background';
    if (['image', 'video', 'sticker'].includes(fbFileType(message.file_type))) classes += ' no-background media-container';

    return classes;
  }

  let classes = message.direction === 'outbound'
    ? `message-by-retailer position-relative f-right ${(message.status === 'error' ? ' error-message' : '')}`
    : 'message-by-customer position-relative';
  classes += ' main-message-container';
  if (message.note === true && !message.order_note) classes += ' note-message';
  if (message.order_note) classes += ' order-note-message';
  if (['voice', 'audio'].includes(wsFileType(message.content_media_type))) {
    classes += ' video-audio audio-background';
  } else if (['image', 'video', 'sticker'].includes(wsFileType(message.content_media_type))) {
    classes += ' no-background media-container';
  }
  return classes;
}

function fbFileType(type) {
  if (!type) return type;
  if (type.includes('image/') || type === 'image') return 'image';

  if (['application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'].includes(type)
    || type === 'file') {
    return 'file';
  }

  if (type.includes('audio/') || type === 'audio') return 'audio';
  if (type.includes('video/') || type === 'video') return 'video';
  if (type.includes('location/') || type === 'location') return 'location';
  if (type === 'fallback') return 'fallback';

  return null;
}

function wsFileType(type = null) {
  if (!type) return type;
  if (type.includes('image/') || type === 'image') return 'image';

  if (['application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'].includes(type)
    || type === 'document') {
    return 'document';
  }

  if (type.includes('audio/') || ['audio', 'voice'].includes(type)) return 'audio';
  if (type.includes('video/') || type === 'video') return 'video';
  if (type === 'sticker') return 'sticker';

  return null;
};

export const formatProductMessage = (product, taxAmount, platform) => {
  const { currency, title, description } = product;
  const effectivePrice = hasDiscount(product) ? Number(product.discounted_price) : Number(product.price);

  let productString = "";
  productString += `${title}\n`;
  productString += `Precio: ${currency}${formatMoney(effectivePrice)}\n`;

  if (product.variant_url) {
    productString += `Link: ${product.variant_url}\n`;
  } else if (product.url) {
    productString += `Link: ${product.url}\n`;
  }

  if (taxAmount && Number(taxAmount) > 0) {
    const totalTaxes = Big(effectivePrice).times(Number(taxAmount)).div(100).toFixed(2);
    const totalPrice = Big(effectivePrice).plus(totalTaxes).toFixed(2);
    productString += `Impuesto (${numberWithOrWithoutDecimal(
      taxAmount
    )}%): ${currency}${formatMoney(totalTaxes)}\n`;
    productString += `Total: ${currency}${formatMoney(totalPrice)}\n`;
  }
  productString += `${formatProductDescription(description, platform)}\n`;
  return productString;
};

export const numberWithOrWithoutDecimal = (number) => {
  const newNumber = Number(number);
  return newNumber % 1 === 0
    ? Number(newNumber.toString())
    : Number(newNumber.toString().replace(/\.0+$/, ""));
};

export const formatProductDescription = (html, platform) => {
  if (isEmpty(html)) return '';

  if (isWhatsapp(platform)) {
    html = html.replace(/<strong>(.*?)<\/strong>|<b>(.*?)<\/b>/gi, '*$1$2*');
    html = html.replace(/<em>(.*?)<\/em>|<i>(.*?)<\/i>/gi, '_$1$2_');
    html = html.replace(/<s>(.*?)<\/s>|<del>(.*?)<\/del>/gi, '~$1$2~');
    html = html.replace(/<code>(.*?)<\/code>|<pre>(.*?)<\/pre>/gi, '`$1$2`');
  }
  html = html.replace(/<ol>(.*?)<\/ol>/gis, (match, content) => {
    let counter = 1;
    return content
      .replace(/<li>(.*?)<\/li>/g, (liMatch, liContent) => {
        const formattedItem = `${counter}. ${liContent.trim()}`;
        counter++;
        return formattedItem + "\n";
      });
  });
  html = html.replace(/<ul>(.*?)<\/ul>/gis, (match, content) => {
    return content
      .replace(/<li>(.*?)<\/li>/g, (liMatch, liContent) => {
        return `• ${liContent.trim()}\n`;
      });
  });
  html = html.replace(/<\/p>/gi, '\n');
  html = html.replace(/<[^>]*>/g, '');
  return html.trim();
};

export const handleOnPasteInput = (e, handleOnChange, otherPasteEvent) => {
  const clipboardData = e.clipboardData || window.clipboardData;
  if (clipboardData.types.indexOf('Files') < 0) {
    e.preventDefault();
    const text = clipboardData.getData('text');
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const textNode = document.createTextNode(text);
    range.insertNode(textNode);
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
    handleOnChange(e);
  } else {
    otherPasteEvent(e);
  }
};
